import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Image, Input, message, Modal, Select, Space, Spin, Switch, Table, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import {
  addCategoryApi,
  CategoryDetail,
  CategoryTreeItem,
  checkCategoryHasGoodsApi,
  deleteCategoryApi,
  getCategoryDetailApi,
  getCategoryTreeApi,
  moveCategoryDownApi,
  moveCategoryUpApi,
  updateCategoryApi,
} from '@/api/pointCenter/category';
import ImageUploader from '@/component/ImageUploader';
import { CATEGORY_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';

const { Text } = Typography;
const { Option } = Select;

const findPathById = (tree: CategoryTreeItem[], targetId?: number | null, path: number[] = []): number[] | null => {
  if (!targetId) return null;
  for (const node of tree) {
    const currentPath = [...path, node.id];
    if (node.id === targetId) return currentPath;
    if (node.children && node.children.length) {
      const childPath = findPathById(node.children, targetId, currentPath);
      if (childPath) return childPath;
    }
  }
  return null;
};

const collectAllIds = (tree: CategoryTreeItem[]): number[] => {
  const ids: number[] = [];
  const walk = (nodes: CategoryTreeItem[]) => {
    nodes.forEach(item => {
      ids.push(item.id);
      if (item.children && item.children.length) {
        walk(item.children);
      }
    });
  };
  walk(tree);
  return ids;
};

const findSiblingInfo = (
  nodes: CategoryTreeItem[],
  targetId: number,
): { siblings: CategoryTreeItem[]; index: number } | null => {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.id === targetId) {
      return { siblings: nodes, index: i };
    }
    if (node.children && node.children.length) {
      const res = findSiblingInfo(node.children, targetId);
      if (res) return res;
    }
  }
  return null;
};

const CategoryManage: React.FC = () => {
  const { currentShopId } = useSnapshot(state);
  const [categoryTree, setCategoryTree] = useState<CategoryTreeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [movingId, setMovingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const isEnabled = useCallback((record: CategoryTreeItem) => {
    if (typeof record.status === 'number') return record.status === 1;
    return false;
  }, []);

  const loadCategoryTree = useCallback(async () => {
    if (!currentShopId) {
      setCategoryTree([]);
      setExpandedRowKeys([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getCategoryTreeApi(currentShopId);
      if (res.code === 200) {
        const data = res.data ?? [];
        setCategoryTree(data);
        setExpandedRowKeys(collectAllIds(data));
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [currentShopId]);

  useEffect(() => {
    loadCategoryTree();
  }, [loadCategoryTree]);

  useEffect(() => {
    form.setFieldsValue({ level: 1, status: true, parentId: undefined, iconUrl: undefined });
  }, [form]);

  const levelOneOptions = useMemo(
    () =>
      (categoryTree || []).map(item => ({
        value: item.id,
        label: item.categoryName,
        children:
          item.children?.map(child => ({
            value: child.id,
            label: child.categoryName,
          })) || [],
      })),
    [categoryTree],
  );

  const handleToggleStatus = useCallback(
    async (record: CategoryTreeItem, checked: boolean) => {
      if (!currentShopId) return;
      setTogglingId(record.id);
      try {
        const detailRes = await getCategoryDetailApi(currentShopId, record.id);
        const detail: CategoryDetail = detailRes?.data || ({} as any);
        const payload = {
          id: detail?.id ?? record.id,
          categoryName: detail?.categoryName || record.categoryName,
          parentId: detail?.level === 1 ? null : (detail?.parentId ?? record.parentId ?? null),
          status: checked ? 1 : 0,
          iconUrl: detail?.iconUrl ?? record.iconUrl,
          description: detail?.description ?? record.description,
        };
        const res = await updateCategoryApi(currentShopId, payload);
        if (res?.code === 200) {
          message.success(checked ? '已显示' : '已隐藏');
          loadCategoryTree();
        }
      } catch (error) {
        console.error('更新显示状态失败', error);
      } finally {
        setTogglingId(null);
      }
    },
    [currentShopId, loadCategoryTree],
  );

  const handleDelete = useCallback(
    async (record: CategoryTreeItem) => {
      if (!currentShopId) return;
      let hasGoods = true;
      try {
        const res = await checkCategoryHasGoodsApi(currentShopId, record.id);
        const data = res?.data ?? res;
        const resultFlag = data?.result ?? data?.hasGoods ?? data?.exist;
        if (typeof resultFlag === 'boolean') {
          hasGoods = resultFlag;
        } else if (typeof data === 'boolean') {
          hasGoods = data;
        } else {
          hasGoods = !!data;
        }
      } catch (error) {
        hasGoods = true;
      }
      Modal.confirm({
        title: '确认删除该类目？',
        content: hasGoods ? '该目录下包含商品，如删除，商品将归属到根目录，确认删除？' : '删除后不可恢复，请确认。',
        okText: '删除',
        cancelText: '取消',
        okButtonProps: { danger: true, loading: deletingId === record.id },
        onOk: async () => {
          setDeletingId(record.id);
          try {
            const res = await deleteCategoryApi(currentShopId, record.id);
            if (res?.code === 200) {
              message.success('删除成功');
              loadCategoryTree();
            }
          } finally {
            setDeletingId(null);
          }
        },
      });
    },
    [currentShopId, deletingId, loadCategoryTree],
  );

  const handleMove = useCallback(
    async (record: CategoryTreeItem, direction: 'up' | 'down') => {
      if (!currentShopId) return;
      setMovingId(record.id);
      try {
        const api = direction === 'up' ? moveCategoryUpApi : moveCategoryDownApi;
        const res = await api(currentShopId, record.id);
        if (res?.code === 200) {
          message.success(direction === 'up' ? '上移成功' : '下移成功');
          loadCategoryTree();
        }
      } finally {
        setMovingId(null);
      }
    },
    [currentShopId, loadCategoryTree],
  );

  const handleOpenAddModal = () => {
    form.resetFields();
    form.setFieldsValue({ level: 1, status: true });
    setModalMode('create');
    setEditingId(null);
    setDetailLoading(false);
    setAddModalVisible(true);
  };

  const handleLevelChange = (value: number) => {
    form.setFieldsValue({ level: value, parentId: undefined, parentPath: undefined });
  };

  const openEditModal = useCallback(
    async (record: CategoryTreeItem) => {
      if (!currentShopId) return;
      setModalMode('edit');
      setEditingId(record.id);
      setDetailLoading(true);
      try {
        const res = await getCategoryDetailApi(currentShopId, record.id);
        const detail: CategoryDetail | undefined = res?.data;
        if (res?.code === 200 && detail) {
          const level = detail.level ?? record.level ?? 1;
          const parentPath = findPathById(categoryTree, detail.parentId) || [];
          const parentValue = level === 1 ? undefined : level === 2 ? parentPath[parentPath.length - 1] : parentPath;
          form.setFieldsValue({
            categoryName: detail.categoryName,
            level,
            parentId: parentValue,
            status: typeof detail.status === 'number' ? detail.status === 1 : false,
            iconUrl: detail.iconUrl,
            description: detail.description,
          });
          setAddModalVisible(true);
        }
      } catch (error) {
        setEditingId(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [categoryTree, currentShopId, form],
  );

  const handleSubmit = async () => {
    if (!currentShopId) return;
    if (modalMode === 'edit' && !editingId) return;
    try {
      const values = await form.validateFields();
      const parentId = Array.isArray(values.parentId) ? values.parentId[values.parentId.length - 1] : values.parentId;
      const payload = {
        categoryName: values.categoryName,
        parentId: values.level === 1 ? null : parentId,
        status: values.status ? 1 : 0,
        iconUrl: values.iconUrl,
        description: values.description,
        id: modalMode === 'edit' ? (editingId ?? undefined) : undefined,
      };
      setSubmitLoading(true);
      const res =
        modalMode === 'edit'
          ? await updateCategoryApi(currentShopId, payload)
          : await addCategoryApi(currentShopId, payload);
      if (res?.code === 200) {
        message.success(modalMode === 'edit' ? '编辑类目成功' : '新增类目成功');
        setAddModalVisible(false);
        setEditingId(null);
        loadCategoryTree();
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: '类目名称',
        dataIndex: 'categoryName',
        key: 'categoryName',
        render: (value: string) => value || '-',
      },
      {
        title: '缩略图',
        dataIndex: 'iconUrl',
        key: 'iconUrl',
        render: (value: string, record: CategoryTreeItem) =>
          value || record.iconUrl ? (
            <Image
              src={value || record.iconUrl}
              width={48}
              height={48}
              preview={{ mask: null }}
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <Text type="secondary">--</Text>
          ),
        width: 120,
      },
      {
        title: '是否显示',
        dataIndex: 'status',
        key: 'status',
        render: (_: unknown, record: CategoryTreeItem) => (
          <Switch
            checked={isEnabled(record)}
            loading={togglingId === record.id}
            onChange={checked => handleToggleStatus(record, checked)}
          />
        ),
        width: 120,
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        render: (value: string) => value || '--',
        width: 200,
      },
      {
        title: '操作',
        key: 'action',
        render: (_: unknown, record: CategoryTreeItem) => (
          <Space size={15}>
            {checkPermissions(CATEGORY_KEY.UPDATE) && (
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => openEditModal(record)}>
                编辑
              </Button>
            )}
            {checkPermissions(CATEGORY_KEY.DELETE) && (
              <Button
                type="link"
                style={{ padding: 0, color: '#ff4d4f' }}
                onClick={() => handleDelete(record)}>
                删除
              </Button>
            )}
            {checkPermissions(CATEGORY_KEY.MOVE) && (
              <Button
                type="link"
                style={{ padding: 0 }}
                disabled={
                  movingId === record.id ||
                  (() => {
                    const info = findSiblingInfo(categoryTree, record.id);
                    return info ? info.index === 0 : false;
                  })()
                }
                onClick={() => handleMove(record, 'up')}>
                上移
              </Button>
            )}
            {checkPermissions(CATEGORY_KEY.MOVE) && (
              <Button
                type="link"
                style={{ padding: 0 }}
                disabled={
                  movingId === record.id ||
                  (() => {
                    const info = findSiblingInfo(categoryTree, record.id);
                    return info ? info.index === info.siblings.length - 1 : false;
                  })()
                }
                onClick={() => handleMove(record, 'down')}>
                下移
              </Button>
            )}
          </Space>
        ),
        width: 240,
      },
    ],
    [categoryTree, handleDelete, handleMove, isEnabled, movingId, openEditModal],
  );

  return (
    <div style={{ padding: 24, background: '#fff' }}>
      <Space style={{ marginBottom: 16 }}>
        {checkPermissions(CATEGORY_KEY.ADD) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenAddModal}>
            创建
          </Button>
        )}
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={categoryTree}
        loading={loading}
        pagination={false}
        bordered
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(prev =>
              expanded ? Array.from(new Set([...prev, record.id])) : prev.filter(key => key !== record.id),
            );
          },
        }}
      />
      <Modal
        title={modalMode === 'edit' ? '编辑类目' : '添加类目'}
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          setEditingId(null);
        }}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        destroyOnHidden
        width={480}
        okText="确认"
        cancelText="取消">
        <Spin spinning={detailLoading}>
          <Form
            form={form}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 16 }}
            initialValues={{ level: 1, status: true }}>
            <Form.Item
              label="类目名称"
              name="categoryName"
              rules={[
                { required: true, message: '请输入名称' },
                { max: 4, message: '最多输入4个字' },
              ]}>
              <Input
                placeholder="请输入"
                maxLength={4}
                showCount
              />
            </Form.Item>
            <Form.Item
              label="类目级别"
              name="level"
              rules={[{ required: true, message: '请选择类目级别' }]}>
              <Select
                placeholder="请选择"
                onChange={handleLevelChange}>
                <Option value={1}>一级类目</Option>
                <Option value={2}>二级类目</Option>
              </Select>
            </Form.Item>
            <Form.Item
              shouldUpdate={(prev, next) => prev.level !== next.level || prev.parentId !== next.parentId}
              noStyle>
              {({ getFieldValue }) => {
                const level = getFieldValue('level');
                const isLevelOne = level === 1;
                return !isLevelOne ? (
                  <Form.Item
                    label="上级类目"
                    name="parentId"
                    rules={[
                      {
                        required: true,
                        message: '请选择上级类目',
                      },
                    ]}>
                    <Select
                      placeholder="请选择"
                      allowClear
                      onChange={value => form.setFieldsValue({ parentId: value })}>
                      {levelOneOptions.map(option => (
                        <Option
                          key={option.value}
                          value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>
            <Form.Item
              label="是否显示"
              name="status"
              valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              label="类目描述"
              name="description"
              rules={[{ max: 500, message: '描述不超过500字符' }]}>
              <Input.TextArea
                placeholder="选填，描述信息"
                showCount
                maxLength={500}
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Form.Item>
            <Form.Item
              label="缩略图"
              name="iconUrl">
              <ImageUploader
                value={form.getFieldValue('iconUrl')}
                onChange={val =>
                  form.setFieldsValue({
                    iconUrl: val?.imageUrl || val,
                  })
                }
                limitWidth={80}
                limitHeight={80}
                maxSizeKB={200}
              />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default CategoryManage;
